import { usePricing } from 'components/Pricing/PricingProvider'
import { type DeliveryPromiseRequest } from 'services/ShippingOptionsManager'
import StorageManager from 'services/StorageManager'
import UserManager from 'services/UserManager'
import { getPricingCountry } from 'services/CountryManager'
import { useKeys } from 'services/KeysProvider'
import { useCartStoreOrNull } from 'stores/cartStore/CartStoreProvider'
import type { CartStore } from 'stores/cartStore/CartStore'
import { SHIPPING_TOP_QUEUE_KEY } from 'stores/shippingStore/shipping.consts'
import { getPartialUploadsInformation } from 'stores/shippingStore/shipping.utils'
import { getCartProductTypes } from 'features/productArrivals/cartShippingOptions'
import { type DeliveryPromisesQueryInputs } from 'features/productArrivals/deliveryPromisesQuery'
import { getUrlParam } from 'utils/UrlUtils'
import { isClient } from 'utils/runtimeUtils'
import { useMobxSelector } from 'utils/mobx/useMobxSelector'

// The cart is checked out as one group order built from its own items, so its promises come
// from the cart too. The tiles come from the checkout payload: the tiles cart item holds only
// pricing and thumbnails.
function getCartDeliveryPromiseRequests(
  cartStore: CartStore
): DeliveryPromiseRequest[] {
  const checkoutPayloads = cartStore.buildCheckoutPayloads()
  return getCartProductTypes(cartStore).map((productType) => ({
    productType,
    uploads: getPartialUploadsInformation({
      orderItems: checkoutPayloads.filter(
        (checkoutPayload) => checkoutPayload.productType === productType
      ),
    }),
  }))
}

export function useDeliveryPromisesQueryInputs(): Omit<
  DeliveryPromisesQueryInputs,
  'address'
> {
  const { pricingVariantsIds } = usePricing()
  const { ipCountry } = useKeys()
  const cartStore = useCartStoreOrNull()
  const deliveryPromiseRequests = useMobxSelector(
    () => (cartStore ? getCartDeliveryPromiseRequests(cartStore) : []),
    []
  )
  // The provider also renders on the server, which has no URL, storage or user to read; the
  // query only runs in the browser.
  const isBrowser = isClient()
  return {
    deliveryPromiseRequests,
    variantsIds: pricingVariantsIds,
    isTopQueueOn:
      isBrowser &&
      !!(getUrlParam('tq') || StorageManager.get(SHIPPING_TOP_QUEUE_KEY)),
    customerEmail: isBrowser ? UserManager.getUserEmail() : undefined,
    pricingCountry: getPricingCountry(ipCountry),
  }
}
